import { useContext, useEffect, useState } from 'react';

import DocumentContext from '../contexts/DocumentContext.context';
import { useBlob, useGetBlob } from '../api';
import type { TDocument, TDraft, TVersion } from '../types';
import { SignInContext } from '@modules/users';
import { getVersionId } from '@modules/items';
import type { TPermissions } from '@modules/access';
import useItemScreenStore from '@modules/items/stores/useItemScreenStore';
import { composeTemplateDocument } from '@shared/backendFrontend';
import flattenTreeHeadings from '../helpers/flattenTreeHeadings.helper';
import { applyImagePreprocessing } from '../components/changesHtmlUtils';
import { resolvePreviousVersionId } from '@modules/items/helpers/resolveDefaultComparisonBase.helper';
import { diffHtmlTree } from '../doc-engine';
import {
  createFrontendRuntime,
  createFrontendContentHooks,
  createFrontendRuntimeSide,
} from '../components/changesRuntime';
import { wholeDocumentHtml } from '../largeDocument';

const CHANGE_TRACKING_MIN_DATE = new Date('2024-10-21T00:00:00.000Z');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const resolvePreviousVersion = (doc: TDocument, version: TVersion | TDraft | undefined): TVersion | undefined => {
  const versionId = version ? getVersionId(version) : 'draft';
  const prevId = resolvePreviousVersionId(doc.versions, versionId);
  return prevId ? doc.versions.find((v) => v.id === prevId) : undefined;
};

const resolvePreviousHtml = async (
  prevVersion: TVersion,
  prevContent: string,
  doc: TDocument,
  permissions: TPermissions,
  getBlob: ReturnType<typeof useGetBlob>,
  isCancelled: () => boolean
): Promise<string> => {
  const prevTemplateVersionId = prevVersion.templateSource?.versionId;
  const prevTemplateBlobName = prevTemplateVersionId
    ? doc.templateBlobInfo?.versionBlobNames?.[prevTemplateVersionId]
    : undefined;

  if (prevVersion.templateSource?.fieldValuesBlobName && prevTemplateBlobName && doc.templateBlobInfo) {
    const [templateHtml, fieldValuesRaw] = await Promise.all([
      getBlob.mutateAsync({
        token: doc.templateBlobInfo.versionBlobToken,
        containerName: doc.templateBlobInfo.containerId,
        blobName: prevTemplateBlobName,
      }),
      getBlob.mutateAsync({
        token: permissions.token,
        containerName: doc._id,
        blobName: prevVersion.templateSource.fieldValuesBlobName,
      }),
    ]);
    if (isCancelled()) return '';
    const fieldValues = typeof fieldValuesRaw === 'string' ? JSON.parse(fieldValuesRaw) : fieldValuesRaw;
    return composeTemplateDocument(templateHtml, fieldValues);
  }

  return prevContent === '<PLACEHOLDER>' ? '' : prevContent;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Computes the diff HTML between the current and previous document version.
 *
 * Handles previous version resolution, template composition, image preprocessing,
 * caption map construction, runtime creation, and diff computation via the engine.
 */
const useDiffComputation = () => {
  const { mock } = useContext(SignInContext).organization;
  const { doc, version, isEdit, content, editor, permissions, treeHeadings } = useContext(DocumentContext);
  const { comparisonBaseVersionId } = useItemScreenStore();

  const prevVersion = comparisonBaseVersionId
    ? doc.versions.find((v) => v.id === comparisonBaseVersionId)
    : resolvePreviousVersion(doc, version);
  const { data: prevContent, error: blobError } = useBlob(permissions.token, doc._id, prevVersion?.blobName);

  const [diffContent, setDiffContent] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();

  const getBlob = useGetBlob();

  useEffect(() => {
    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setErrorMessage(undefined);
      if (typeof prevContent !== 'string' || typeof content !== 'string' || !version) return;

      try {
        if (!prevVersion) {
          return setErrorMessage('This is the first version. There is no previous version to compare against.');
        }

        if (new Date(prevVersion.createdDate) < CHANGE_TRACKING_MIN_DATE) {
          return setErrorMessage(
            'Change tracking is only available between two versions created on or after 2024/10/21.'
          );
        }

        const prevHtml = await resolvePreviousHtml(
          prevVersion,
          prevContent,
          doc,
          permissions,
          getBlob,
          () => cancelled
        );
        if (cancelled) return;

        // The editor holds one PART of a document loaded in parts, so `getHTML()` would present the
        // rest of the document as deleted - on the surface an approver signs off from. Same seam the
        // save path uses: it folds the live part back in and answers for the whole document.
        const rawHtml = isEdit ? (wholeDocumentHtml(editor) ?? editor.getHTML()) : content;
        const preparedPrevHtml = applyImagePreprocessing(doc._id, permissions.token, prevHtml);
        const preparedHtml = applyImagePreprocessing(doc._id, permissions.token, rawHtml);

        const currentHeadings = treeHeadings.length > 0 ? flattenTreeHeadings(treeHeadings) : version.headings;
        const previousHeadings = prevVersion.headings ?? [];
        const runtime = createFrontendRuntime(
          doc,
          createFrontendRuntimeSide(doc, version, preparedHtml, currentHeadings),
          createFrontendRuntimeSide(doc, prevVersion, preparedPrevHtml, previousHeadings),
          getBlob
        );

        const opts = { isH5RefEnabled: mock.isH5RefEnabled };
        const hooks = createFrontendContentHooks();
        const result = await diffHtmlTree(preparedPrevHtml, preparedHtml, { runtime, opts, hooks });

        if (cancelled) return;
        setDiffContent(result.html);
      } catch (error) {
        console.error(error);
        if (cancelled) return;
        setErrorMessage('Failed to calculate changes.');
      }
    }, 10);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    content,
    prevContent,
    version,
    treeHeadings,
    permissions.token,
    mock.isH5RefEnabled,
    isEdit,
    doc._id,
    comparisonBaseVersionId,
  ]);

  return { diffContent, errorMessage, blobError };
};

export default useDiffComputation;
